import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Home, Search, Plus, LayoutDashboard, ListChecks, User, Shield, LogOut, Settings, Truck, Globe, Gift, Wallet, HelpCircle, Repeat, Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import LiveActivity from "@/components/LiveActivity";
import PWAInstall from "@/components/PWAInstall";
import BidAlert from "@/components/BidAlert";

export default function AppLayout() {
  const { user, logout, refresh } = useAuth();
  const { lang, toggle, t } = useLang();
  const navigate = useNavigate();
  const loc = useLocation();
  const isAdmin = user?.role === "admin";
  const mode = user?.active_mode === "driver" ? "driver" : "customer";

  const switchMode = async () => {
    if (!user) return;
    if (mode === "customer" && !user.is_driver) { navigate("/bliv-grabber"); return; }
    const next = mode === "customer" ? "driver" : "customer";
    try {
      await api.post("/profile/mode", { mode: next });
      await refresh();
      toast.success(next === "driver" ? "Du er nu i chauffør-tilstand 🚚" : "Du er nu i kunde-tilstand 🛋️");
      navigate(next === "driver" ? "/jobs" : "/dashboard");
    } catch {
      toast.error("Kunne ikke skifte tilstand");
    }
  };

  const initials = (user?.full_name || user?.email || "?").split(/\s+/).map(s => s[0]).slice(0,2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/60 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-sm shadow-primary/30 group-hover:scale-105 transition-transform">
              <Truck className="w-5 h-5 text-primary-foreground" strokeWidth={2.2} />
            </div>
            <div>
              <span className="font-bold text-lg tracking-tight">Grabber</span>
              <span className="text-[10px] text-muted-foreground ml-1 hidden sm:inline">.dk</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {!user && (
              <>
                <NavLink to="/jobs" current={loc.pathname} icon={<Search className="w-4 h-4" />}>{t("nav.find_jobs")}</NavLink>
                <NavLink to="/post" current={loc.pathname} icon={<Plus className="w-4 h-4" />}>{t("nav.post_job")}</NavLink>
              </>
            )}
            {user && mode === "customer" && (
              <>
                <NavLink to="/post" current={loc.pathname} icon={<Plus className="w-4 h-4" />}>{t("nav.post_job")}</NavLink>
                <NavLink to="/my-postings" current={loc.pathname} icon={<Briefcase className="w-4 h-4" />}>Mine opslag</NavLink>
                <NavLink to="/dashboard" current={loc.pathname} icon={<LayoutDashboard className="w-4 h-4" />}>{t("nav.dashboard")}</NavLink>
              </>
            )}
            {user && mode === "driver" && (
              <>
                <NavLink to="/jobs" current={loc.pathname} icon={<Search className="w-4 h-4" />}>{t("nav.find_jobs")}</NavLink>
                <NavLink to="/my-jobs" current={loc.pathname} icon={<ListChecks className="w-4 h-4" />}>Mine opgaver</NavLink>
                <NavLink to="/earnings" current={loc.pathname} icon={<Wallet className="w-4 h-4" />}>Indtægter</NavLink>
                <NavLink to="/dashboard" current={loc.pathname} icon={<LayoutDashboard className="w-4 h-4" />}>{t("nav.dashboard")}</NavLink>
              </>
            )}
            {isAdmin && <NavLink to="/admin" current={loc.pathname} icon={<Shield className="w-4 h-4" />}>{t("nav.admin")}</NavLink>}
          </nav>

          <div className="flex items-center gap-1">
            <button
              onClick={toggle}
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold hover:bg-muted transition-colors uppercase tracking-wider"
              data-testid="lang-toggle"
              aria-label="Toggle language"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === "da" ? "DA" : "EN"}
            </button>
            <NotificationBell />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-muted transition-colors" data-testid="user-menu-button">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">{initials}</AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:flex flex-col items-start leading-tight">
                      <span className="text-sm font-medium max-w-[120px] truncate">{user.full_name || user.email}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${mode === "driver" ? "text-emerald-600" : "text-primary"}`} data-testid="mode-indicator">
                        {mode === "driver" ? "Chauffør" : "Kunde"}
                      </span>
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuItem onClick={switchMode} data-testid="menu-switch-mode" className="bg-primary/5 font-semibold focus:bg-primary/10 py-3">
                    <Repeat className="w-4 h-4 mr-2 text-primary" />
                    {mode === "driver" ? "Skift til kunde 🛋️" : user.is_driver ? "Skift til chauffør 🚚" : "Bliv grabber, tjen penge 🚀"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`/driver/${encodeURIComponent(user.email)}`)} data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" /> {t("nav.profile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/profile/edit")} data-testid="menu-edit-profile">
                    <Settings className="w-4 h-4 mr-2" /> {t("nav.edit_profile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/my-postings")}>
                    <Briefcase className="w-4 h-4 mr-2" /> Mine opslag
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/my-jobs")}>
                    <ListChecks className="w-4 h-4 mr-2" /> Mine opgaver
                  </DropdownMenuItem>
                  {user.is_driver && (
                    <DropdownMenuItem onClick={() => navigate("/earnings")} data-testid="menu-earnings">
                      <Wallet className="w-4 h-4 mr-2" /> Mine indtægter
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/referrals")} data-testid="menu-referrals">
                    <Gift className="w-4 h-4 mr-2 text-primary" /> Inviter & tjen
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/faq")} data-testid="menu-faq">
                    <HelpCircle className="w-4 h-4 mr-2" /> FAQ & hjælp
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/admin")}><Shield className="w-4 h-4 mr-2" /> {t("nav.admin")}</DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="login-btn">{t("nav.login")}</Button>
                <Button size="sm" onClick={() => navigate("/register")} data-testid="register-btn">{t("nav.register")}</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 pb-24 md:pb-12">
        <Outlet />
      </main>

      {/* Live activity feed (only on home) */}
      {loc.pathname === "/" && <LiveActivity />}

      {/* PWA install prompt (chromium-based browsers) */}
      <PWAInstall />
      <BidAlert />

      {/* Footer */}
      <footer className="border-t border-border bg-background hidden md:block">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center">
              <Truck className="w-3.5 h-3.5 text-primary-foreground" />
            </div>
            <span className="text-sm font-semibold">Grabber</span>
            <span className="text-muted-foreground text-sm">© 2026</span>
          </div>
          <div className="flex gap-6 text-sm text-muted-foreground">
            <Link to="/#tryg-betaling" className="hover:text-foreground transition-colors">Tryg betaling</Link>
            <Link to="/faq" className="hover:text-foreground transition-colors">FAQ</Link>
            <Link to="/om-os" className="hover:text-foreground transition-colors">Om os</Link>
            <Link to="/vilkaar" className="hover:text-foreground transition-colors">Vilkår</Link>
            <Link to="/privatliv" className="hover:text-foreground transition-colors">Privatliv</Link>
            <Link to="/support" className="hover:text-foreground transition-colors">Support</Link>
          </div>
        </div>
      </footer>

      {/* Bottom nav (mobile) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-xl border-t border-border safe-area-bottom">
        {!user && (
          <div className="grid grid-cols-4 h-16">
            <BottomTab to="/" current={loc.pathname} icon={<Home className="w-5 h-5" />} label="Hjem" />
            <BottomTab to="/jobs" current={loc.pathname} icon={<Search className="w-5 h-5" />} label="Find" />
            <BottomTab to="/post" current={loc.pathname} icon={<Plus className="w-6 h-6" />} label="Opret" primary />
            <BottomTab to="/login" current={loc.pathname} icon={<User className="w-5 h-5" />} label="Log ind" />
          </div>
        )}
        {user && mode === "customer" && (
          <div className="grid grid-cols-4 h-16">
            <BottomTab to="/" current={loc.pathname} icon={<Home className="w-5 h-5" />} label="Hjem" />
            <BottomTab to="/post" current={loc.pathname} icon={<Plus className="w-6 h-6" />} label="Opret" primary />
            <BottomTab to="/my-postings" current={loc.pathname} icon={<Briefcase className="w-5 h-5" />} label="Opslag" />
            <BottomTab to="/dashboard" current={loc.pathname} icon={<LayoutDashboard className="w-5 h-5" />} label="Mig" />
          </div>
        )}
        {user && mode === "driver" && (
          <div className="grid grid-cols-5 h-16">
            <BottomTab to="/" current={loc.pathname} icon={<Home className="w-5 h-5" />} label="Hjem" />
            <BottomTab to="/jobs" current={loc.pathname} icon={<Search className="w-6 h-6" />} label="Find" primary />
            <BottomTab to="/my-jobs" current={loc.pathname} icon={<ListChecks className="w-5 h-5" />} label="Opgaver" />
            <BottomTab to="/earnings" current={loc.pathname} icon={<Wallet className="w-5 h-5" />} label="Indtægter" />
            <BottomTab to="/dashboard" current={loc.pathname} icon={<LayoutDashboard className="w-5 h-5" />} label="Mig" />
          </div>
        )}
      </nav>
    </div>
  );
}

function NavLink({ to, current, children, icon }) {
  const active = to === "/" ? current === "/" : current.startsWith(to);
  return (
    <Link
      to={to}
      data-testid={`nav-${to.replace(/\W+/g, "")}`}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"
      }`}
    >
      {icon}{children}
    </Link>
  );
}

function BottomTab({ to, current, icon, label, primary }) {
  const active = to === "/" ? current === "/" : current.startsWith(to);
  return (
    <Link to={to} className={`flex flex-col items-center justify-center gap-0.5 ${active ? "text-primary" : "text-muted-foreground"}`}>
      <div className={`${primary ? "w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center -mt-3 shadow-lg shadow-primary/30" : ""}`}>
        {icon}
      </div>
      <span className={`text-[10px] font-medium ${primary ? "mt-0" : ""}`}>{label}</span>
    </Link>
  );
}
